//it is assumed that each layer is a feature layer and has a serviceLayer property
/**
 * Utility class which is used to configure and execute queries on ESRI feature layers.
 * @module app/models/map/MapQuery
 */
import _ from "underscore";
import $ from "jquery";
import async from "async";
import GeoUtils from "app/utils/GeoUtils";

var gsvc;
var initialize = _.once(function () {
  var GeometryService = window.require("esri/tasks/GeometryService");
  var esriConfig = window.require("esri/config");
  esriConfig.defaults.io.corsEnabledServers.push("apps.hbkengineering.com");

  esriConfig.defaults.io.proxyUrl =
    "https://apps.hbkengineering.com/api/arctoken/proxy";
  esriConfig.defaults.io.alwaysUseProxy = false;

  gsvc = new GeometryService(
    "https://tasks.arcgisonline.com/ArcGIS/rest/services/Geometry/GeometryServer"
  );
});

/**
 * Constructor. Creates a new map query instance.
 * @param {Array|FeatureLayer} layers The layer or layers (descending from MapLayerBase) to consider in the query.
 */
function MapQuery(layers) {
  this.layers = layers;

  if (!_.isArray(this.layers)) {
    this.layers = [this.layers];
  }

  initialize();
}

var whereTemplate = _.template(
  "<%if(objectId){%>(<%=objectIdField%> > <%=objectId%>)<%if(oldWhere){%> AND <%}%><%}%><%if(oldWhere){%>(<%=oldWhere%>)<%}%>"
);
var geometryTypeMap = {
  esriGeometryPoint: "point",
  esriGeometryPolyline: "polyline",
  esriGeometryPolygon: "polygon",
};

function transformDistance(pt, distance) {
  return (distance * 1) / Math.cos((pt.getLatitude() * Math.PI) / 180); //distance * distortion
}

//create a new query object, querying on the given ids, the given spatial constraint, the given where clause,
//starting at the given startId, exclusive. Parameters that are not needed should be passed as a falsy value
function buildQuery(objectIdField, ids, spatial, where, distance, startId, sr) {
  var GeometryService = window.require("esri/tasks/GeometryService");
  var Query = window.require("esri/tasks/query");
  var BufferParameters = window.require("esri/tasks/BufferParameters");
  var deferred = new $.Deferred();
  var query = new Query();

  if (ids) {
    query.objectIds = ids;
  }

  if (where || startId) {
    query.where = whereTemplate({
      objectIdField: objectIdField,
      objectId: startId,
      oldWhere: where,
    });
  }

  if (_.isNumber(distance)) {
    query.distance = distance;
  }

  if (sr) {
    query.outSpatialReference = sr;
  }

  query.outFields = ["*"];
  query.returnGeometry = true;

  if (spatial) {
    if (_.isNumber(spatial.distance)) {
      var params = new BufferParameters();
      var center = GeoUtils.getCenter(spatial.geometry);

      params.distances = [transformDistance(center, spatial.distance)];
      params.geometries = [spatial.geometry];
      params.unit = GeometryService.UNIT_FOOT;
      params.outSpatialReference = sr;
      params.bufferSpatialReference = sr;
      gsvc.simplify(params.geometries, function (geometries) {
        params.geometries = geometries;
        gsvc.buffer(
          params,
          function (geometries) {
            query.geometry = geometries[0];
            query.spatialRelationship = spatial.relationship;

            if (spatial.spatialQuery) {
              query.relationParam = spatial.spatialQuery;
            }

            deferred.resolve(query);
          },
          function (err) {
            deferred.reject(err);
          }
        );
      });
    } else {
      query.geometry = spatial.geometry;
      query.spatialRelationship = spatial.relationship;

      if (spatial.spatialQuery) {
        query.relationParam = spatial.spatialQuery;
      }

      deferred.resolve(query);
    }
  } else {
    deferred.resolve(query);
  }

  return deferred.promise();
}

function doHighlight(symbolFactory, self, selectionType) {
  var FeatureLayer = window.require("esri/layers/FeatureLayer");
  var deferred = new $.Deferred();
  var allFeaturesFound = new Map();

  async.each(
    self.layers,
    function (featureLayerWrapper, cb) {
      var featureLayer = featureLayerWrapper.layer;

      if (featureLayer.selectFeatures) {
        //it might be a service layer
        if (symbolFactory) {
          featureLayer.setSelectionSymbol(
            symbolFactory.byType(geometryTypeMap[featureLayer.geometryType])
          );
        }
        //now, we need to make sure we get ALL the features, not just the first portion up to max query results.
        //so, as long as the previous result set is the size of the max record count for the service,
        //we attempt to get the next page. we assume that if the page contains less than the max record count
        //that it is the last page.
        var objectIdField = featureLayer.objectIdField;
        var startId;
        var previousRecordCount = 0;

        var featureName = featureLayer.id;
        if (!allFeaturesFound.has(featureName))
          allFeaturesFound.set(featureName, []);
        var featuresFound = allFeaturesFound.get(featureName);

        async.doWhilst(
          function (cb) {
            self
              .query(objectIdField, startId)
              .done(function (query) {
                featureLayer.selectFeatures(
                  query,
                  selectionType,
                  function (featureSet) {
                    _.each(featureSet, function (feature) {
                      if (feature.getLayer()) {
                        feature.originalLayer = featureLayerWrapper;
                        featuresFound.push(feature);
                      }
                    });
                    cb();
                  },
                  cb
                );
                self.geometry = query.geometry;
              })
              .fail(function (err) {
                cb(err);
              });
          },
          function () {
            var result = false;

            if (
              featuresFound.length - previousRecordCount ==
              featureLayer.serviceLayer.getMaxRecordCount()
            ) {
              objectIdField = featureLayer.objectIdField;
              startId = _.last(featuresFound).attributes[
                featureLayer.objectIdField
              ];
              previousRecordCount = featuresFound.length;

              if (selectionType == FeatureLayer.SELECTION_NEW) {
                selectionType = FeatureLayer.SELECTION_ADD;
              }

              result = true; //do another query
            }

            return result;
          },
          function (err) {
            if (err) {
              return cb(err);
            }

            cb();
          }
        );
      } else {
        cb();
      }
    },
    function (err) {
      if (err) {
        return deferred.reject(err);
      }

      var featuresFound = [];
      allFeaturesFound.forEach(function (features) {
        featuresFound = featuresFound.concat(features);
      });

      deferred.resolve(featuresFound);
    }
  );

  return deferred;
}

MapQuery.prototype = {
  /**
   * Specify the id or ids on which to query.
   * @param  {Array|Integer} ids The feature ids to query.
   * @return {MapQuery}     A reference to `this`.
   */
  ids: function (ids) {
    if (!_.isArray(ids)) {
      ids = [ids];
    }

    if (!this._ids) {
      this._ids = [];
    }

    this._ids = _.union(this._ids, ids);

    return this;
  },

  /**
   * Specify the spatial reference to use for querying.
   * @param  {esri/SpatialReference} sr The spatial reference.
   * @return {MapQuery}    A reference to `this`.
   */
  reference: function (sr) {
    if (sr) {
      this._sr = sr;
    }

    return this;
  },

  //create a boundary query
  /**
   * Specify the parameters for a boundary-based query. This is a spatial query in which any other geometry within a given distance of the input geometry is returned in the results.
   * @param  {esri/geometry/Geometry} geometry     The geometry on which to query.
   * @param  {Number} distance     The distance, in feet, from the geometry boundaries to which to query.
   * @param  {string} relationship One of the constants in esri/tasks/Query
   * @param  {string} spatialQuery For SPATIAL_REL_RELATION, the query parameters.
   * @return {MapQuery}              A reference to `this`.
   */
  boundary: function (geometry, distance, relationship, spatialQuery) {
    this._spatial = {
      geometry: geometry,
      distance: distance,
      relationship: relationship,
    };

    if (spatialQuery) {
      this._spatial.spatialQuery = spatialQuery;
    }

    this.geometry = geometry;

    return this;
  },

  /**
   * Specify the parameters for a standard spatial query.
   * @param  {esri/geometry/Geometry} geometry     The geometry on which to query.
   * @param  {string} relationship One of the constants in esri/tasks/Query
   * @param  {string} spatialQuery For SPATIAL_REL_RELATION, the query parameters.
   * @return {MapQuery}              A reference to `this`.
   */
  spatial: function (geometry, relationship, spatialQuery) {
    this._spatial = {
      geometry: geometry,
      relationship: relationship,
    };

    if (spatialQuery) {
      this._spatial.spatialQuery = spatialQuery;
    }

    this.geometry = geometry;

    return this;
  },

  /**
   * The where clause for the query.
   * @param  {string} where See https://developers.arcgis.com/javascript/jsapi/query-amd.html#where
   * @return {MapQuery}       A reference to `this`.
   */
  where: function (where) {
    this._where = where;

    return this;
  },

  /**
   * The distance for the query. Setting this for a spatial query is *not* equivalent to creating a boundary query.
   * @param  {Number} distance The distance. See https://developers.arcgis.com/javascript/jsapi/query-amd.html#distance
   * @return {MapQuery}          A reference to `this`.
   */
  distance: function (distance) {
    this._distance = distance;

    return this;
  },

  /**
   * Creates the query object that will be used for this operation.
   * @param  {Integer} objectIdField The name of the field which uniquely identifies each feature.
   * @param  {Integer} [startId]       The ID at which to start the query.
   * @return {esri/tasks/Query}               A query object.
   */
  query: function (objectIdField, startId) {
    return buildQuery(
      objectIdField,
      this._ids,
      this._spatial,
      this._where,
      this._distance,
      startId,
      this._sr
    );
  },

  /**
   * Executes the query to find *all* matching items. This may be a large number.
   * @return {Promise} On failure, this will reject with a single error argument. On success, this will resolve with the array of features returned. Each of these objects will be given a property, `originalLayer`, which points to the `FeatureLayer` to which they belong.
   */
  execute: function () {
    var QueryTask = window.require("esri/tasks/QueryTask");
    this._latestMethod = "execute";
    this._latestArgs = arguments;
    var deferred = new $.Deferred();
    var self = this;

    var featuresFound = [];

    async.each(
      this.layers,
      function (featureLayerWrapper, cb) {
        var featureLayer = featureLayerWrapper.layer;

        if (featureLayer.queryFeatures) {
          //it might be a service layer
          //now, we need to make sure we get ALL the features, not just the first portion up to max query results.
          //so, as long as the previous result set is the size of the max record count for the service,
          //we attempt to get the next page. we assume that if the page contains less than the max record count
          //that it is the last page.
          var objectIdField = featureLayer.objectIdField;
          var startId;
          var queryTask = new QueryTask(featureLayer.url);
          var previousRecordCount = 0;

          async.doWhilst(
            function (cb) {
              self
                .query(objectIdField, startId)
                .done(function (query) {
                  queryTask.execute(
                    query,
                    function (featureSet) {
                      _.each(featureSet.features, function (feature) {
                        feature.originalLayer = featureLayerWrapper;
                        featuresFound.push(feature);
                      });
                      cb();
                    },
                    cb
                  );
                  self.geometry = query.geometry;
                })
                .fail(function (err) {
                  cb(err);
                });
            },
            function () {
              var result = false;

              if (
                featuresFound.length - previousRecordCount ==
                featureLayer.serviceLayer.maxRecordCount
              ) {
                objectIdField = featureLayer.objectIdField;
                startId = _.last(featuresFound).attributes[
                  featureLayer.objectIdField
                ];
                previousRecordCount = featuresFound.length;
                result = true; //do another query
              }

              return result;
            },
            function (err) {
              if (err) {
                return cb(err);
              }

              cb();
            }
          );
        } else {
          cb();
        }
      },
      function (err) {
        if (err) {
          return deferred.reject(err);
        }

        deferred.resolve(featuresFound);
      }
    );

    return deferred;
  },

  /**
   * Similar to `execute`. Also changes the `esri.Graphic` representing each geometry to use the symbol returned by `symbols`.
   * @param  {Function} symbols A function accepting an argument of `'point'`, `'polygon'`, or `'polyline'` which returns the `esri.symbols.Symbol` to use in the operation.
   * @return {Promise}         On failure, this will reject with a single error argument. On success, this will resolve with the array of features returned. Each of these objects will be given a property, `originalLayer`, which points to the `FeatureLayer` to which they belong.
   */
  highlight: function (symbols) {
    var FeatureLayer = window.require("esri/layers/FeatureLayer");
    this._latestMethod = "highlight";
    this._latestArgs = arguments;

    return doHighlight(symbols, this, FeatureLayer.SELECTION_NEW);
  },

  /**
   * Executes the query to find matching items.
   * Similar to execute, but only finds items up to the maxRecordCount of the layer, and no more.
   * Used when searching a segment of the entire featureSet, or when the user wants to return the features gradually instead of all at once.
   * @return {Promise} On failure, this will reject with a single error argument. On success, this will resolve with the array of features returned.
   */
  executeThrottle: function (startId) {
    var QueryTask = window.require("esri/tasks/QueryTask");
    var deferred = new $.Deferred();
    this._latestMethod = "execute";
    this._latestArgs = arguments;
    var self = this;
    var featuresFound = [];

    async.each(
      this.layers,
      function (featureLayerWrapper, cb) {
        var featureLayer = featureLayerWrapper.layer;

        //Since we may be querying multiple layers. Only send the query if this particular layer has more features than the starting query ID.
        if (featureLayer.featureCount && featureLayer.featureCount < startId) {
          cb();
        } else {
          var objectIdField = featureLayer.objectIdField;
          var queryTask = new QueryTask(featureLayer.url);

          self
            .query(objectIdField, startId)
            .then(function (query) {
              queryTask.execute(
                query,
                function (featureSet) {
                  featuresFound = featuresFound.concat(featureSet.features);
                  cb();
                },
                function (err) {
                  cb(err);
                }
              );
            })
            .fail(function (err) {
              cb(err);
            });
        }
      },
      function (err) {
        if (err) {
          return deferred.reject(err);
        }
        deferred.resolve(featuresFound);
      }
    );

    return deferred;
  },

  /**
   * Executes the query to find the feature count of each layer.
   * Sets featureCount as an attribute of each featureLayer.
   * @return {Promise} On failure, this will reject with a single error argument. On success, this will resolve with highest featureCount of any of the queryable layers.
   */
  executeForCount: function () {
    var QueryTask = window.require("esri/tasks/QueryTask");
    var deferred = new $.Deferred();
    var self = this;
    var arrayFeatureCounts = [];

    async.each(
      this.layers,
      function (featureLayerWrapper, cb) {
        var featureLayer = featureLayerWrapper.layer;
        var objectIdField = featureLayer.objectIdField;
        var queryTask = new QueryTask(featureLayer.url);

        self.query(objectIdField).done(function (query) {
          queryTask.executeForCount(
            query,
            function (featureCount) {
              featureLayer.featureCount = featureCount;
              arrayFeatureCounts.push(featureCount);
              cb();
            },
            function (err) {
              cb(err);
            }
          );
        });
      },
      function (err) {
        if (err) {
          return deferred.reject(err);
        }
        deferred.resolve(_.max(arrayFeatureCounts));
      }
    );

    return deferred;
  },

  /**
   * Load every feature from the ESRI service into the client model
   * @return {Promise} On failure, rejects with the error, the layer that failed, the number of features successfully processed, and the epxected number of features. On success, resolves with no arguments.
   */
  loadAll: function (maxRecords) {
    /**
     * Snapshot mode being enabled:
     * All features are loaded at outset, instead of being loaded every time the extent of a map changes.
     * The esri server, as currently set up, will only return the first 1000 objects.
     * Send a request to get the featureRecordCount from the Esri Server.
     * Then, query 1000 of these features at a time.
     * Ex: If there are 48,000 features within an external service.
     * Get features 0 to 1000, 1001 to 2000, until have all 48,000 features.
     * Add the features to the map as they are being returned.
     */

    var SpatialReference = window.require("esri/SpatialReference");
    var Graphic = window.require("esri/graphic");
    var deferredQuery = new $.Deferred();
    var self = this;

    // Changes the URL to be directed at the GIS proxy.
    /*function getUrl(url){
		 var parser = new URI(url);

		 var hostId = Math.floor(Math.random() * 50);

		 parser.hostname(hostId.toString() + '.arcgis.hbkapps.com');
		 url = parser.href();

		 return url;
		 }*/

    async.each(
      this.layers,
      function (featureLayerWrapper, cb) {
        var featureLayer = featureLayerWrapper.layer;
        var totalFeatures = 0;

        self.where("1 = 1");
        self
          .executeForCount()
          .then(function (count) {
            var topRanges = [];
            var throttleAmount = maxRecords;

            totalFeatures = count;

            //Breaks the total count into an array of ranges, used for querying x(throttleAmount) number of features at a time.
            function buildThrottleRanges(count) {
              if (totalFeatures < count) {
                return topRanges;
              } else {
                topRanges.push(count);
                return buildThrottleRanges(count + throttleAmount);
              }
            }

            /**
             * If a reference to the map spatialReference exists, set the query spatial reference to the same as the map. Better than using the default spatialReference or the spatialReference of the FeatureLayer.
             * If graphics are in a different spatial reference than the map is, they will be reprojected at every extent change.
             */
            if (featureLayer._map && featureLayer._map.spatialReference) {
              self.reference(
                new SpatialReference(featureLayer._map.spatialReference)
              );
            }

            /**
             * Gets total features in the layer.
             * Using intervals of maxRecordCount, adds graphics as each request is made.
             * Limits to 10 requests at a time(arbitrarily chose 10) so requests do not time out.
             * If fails, notifies the user of the error and that x number of graphics out of total have been added.
             */
            async.eachLimit(
              buildThrottleRanges(0),
              10,
              function (startId, cb) {
                self
                  .executeThrottle(startId)
                  .then(function (featuresReturned) {
                    _.each(featuresReturned, function (individualFeature) {
                      var graphic = new Graphic();

                      graphic.geometry = individualFeature.geometry;
                      graphic.attributes = individualFeature.attributes;
                      graphic.originalLayer = featureLayerWrapper;
                      featureLayer.add(graphic);
                    });
                    cb();
                  })
                  .fail(function (err) {
                    cb(err);
                  });
              },
              function (error) {
                if (error) {
                  error.featuresProcessed = featureLayer.graphics
                    ? featureLayer.graphics.length
                    : 0;
                  error.totalFeatures = count;
                  error.featureLayer = featureLayer;

                  return cb(error);
                }

                cb();
              }
            );
          })
          .fail(function (err) {
            err.featuresProcessed = featureLayer.graphics
              ? featureLayer.graphics.length
              : 0;
            err.totalFeatures = "unknown";
            err.featureLayer = featureLayer;

            return cb(err);
          });
      },
      function (err) {
        if (err) {
          return deferredQuery.reject(
            err,
            err.featureLayer,
            err.featuresProcessed,
            err.totalFeatures
          );
        }

        deferredQuery.resolve();
      }
    );

    return deferredQuery;
  },

  //unselects results by clearing the selection on each layer queried
  //failure callbacks receive a single error argument, success callbacks receive nothing
  //returns deferred
  /**
   * The inverse of `highlight` - any features matching the query have their symbols reverted to the original state.
   * @return {Promise}         On failure, this will reject with a single error argument. On success, this will resolve with no arguments.
   */
  unhighlight: function () {
    var FeatureLayer = window.require("esri/layers/FeatureLayer");
    this._latestMethod = "highlight";
    this._latestArgs = arguments;

    return doHighlight(null, this, FeatureLayer.SELECTION_SUBTRACT);
  },

  refresh: function () {
    if (this._latestMethod) {
      return MapQuery.prototype[this._latestMethod].apply(
        this,
        this._latestArgs
      );
    }
  },
};

export default MapQuery;



//////////////////
// WEBPACK FOOTER
// ./public/js/app/models/map/MapQuery.js
// module id = 138
// module chunks = 1